import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';
import { LoadingSpinner } from '../components';

const SupportTicketDetailPage = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/customer/login'); return; }
    if (!ticketId) return;
    setLoading(true);
    Promise.all([
      apiService.getSupportTicket(ticketId),
      apiService.getSupportMessages(ticketId),
    ]).then(([ticketRes, messagesRes]) => {
      setTicket(ticketRes?.data?.ticket || ticketRes?.data || null);
      const msgs = messagesRes?.data?.messages || messagesRes?.data || [];
      setMessages(Array.isArray(msgs) ? msgs : []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [ticketId, isAuthenticated]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const res = await apiService.addSupportMessage(ticketId, { message: newMessage });
      if (res?.success !== false) {
        const msg = res?.data?.message || { message: newMessage, sender_type: 'customer', created_at: new Date().toISOString() };
        setMessages(prev => [...prev, msg]);
        setNewMessage('');
      } else {
        alert(res?.error || 'Failed to send message');
      }
    } catch (e) {
      alert('Network error');
    } finally {
      setSending(false);
    }
  };

  const STATUS_COLOR = { open: '#3B82F6', in_progress: '#F59E0B', resolved: '#10B981', closed: '#888' };

  if (loading) return <div className="page-container"><LoadingSpinner /></div>;

  return (
    <div className="ticket-detail-page" style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <div className="page-header" style={{ flexShrink: 0 }}>
        <button className="back-btn" onClick={() => navigate('/customer/support')}>← Back</button>
        <div style={{ flex: 1 }}>
          <h2 className="page-title" style={{ fontSize: 16, margin: 0 }}>{ticket?.subject || 'Ticket'}</h2>
          {ticket?.status && (
            <span style={{ fontSize: 12, color: STATUS_COLOR[ticket.status] || '#888', fontWeight: 600 }}>
              {ticket.status.replace(/_/g, ' ')}
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>No messages yet</p>
        ) : (
          messages.map((msg, idx) => {
            const isCustomer = msg.sender_type === 'customer' || msg.from_customer;
            return (
              <div key={idx} style={{ display: 'flex', justifyContent: isCustomer ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '70%',
                  background: isCustomer ? 'var(--primary)' : '#fff',
                  color: isCustomer ? '#fff' : 'var(--text-primary)',
                  padding: '12px 16px',
                  borderRadius: isCustomer ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                  fontSize: 14,
                  lineHeight: 1.5,
                  boxShadow: 'var(--shadow-sm)',
                }}>
                  {msg.message}
                  <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                    {msg.created_at ? new Date(msg.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Reply Input */}
      {ticket?.status !== 'closed' && ticket?.status !== 'resolved' && (
        <div style={{ background: '#fff', borderTop: '1px solid var(--border)', padding: '12px 16px', display: 'flex', gap: 10 }}>
          <input
            type="text"
            className="form-input"
            placeholder="Type a message..."
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            style={{ flex: 1, marginBottom: 0 }}
          />
          <button className="btn-primary" onClick={handleSend} disabled={sending || !newMessage.trim()} style={{ padding: '12px 20px' }}>
            {sending ? '...' : 'Send'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SupportTicketDetailPage;
